import type { ReactNode } from 'react'
import { Button, Table } from '@heroui/react'

import { ChevronLeftIcon, ChevronRightIcon } from '@/components/ui/icons'

import { quantity, riel, share, usd, whole } from './report-format'

type CellValue = number | string | null | undefined

export interface ReportColumn<T> {
  id: string
  header: string
  /** How the default cell prints `value`. Numeric formats right-align. */
  format?: 'text' | 'usd' | 'riel' | 'whole' | 'quantity' | 'share'
  value: (row: T) => CellValue
  /** Overrides the default cell. `value` still decides whether the column is empty. */
  render?: (row: T) => ReactNode
  /**
   * Dropped when every row holds nothing — zero, blank or null.
   *
   * The legacy pack printed VAT, specific tax and service charge as columns of
   * 0.00 on every report, every day. A column that can only ever say zero
   * teaches a reader to skip columns, and the one they skip next will not be
   * zero.
   */
  hideWhenEmpty?: boolean
  /** Shown only when this holds for the rows — for a column that depends on another one. */
  visibleWhen?: (rows: T[]) => boolean
}

export interface ReportRow<T> {
  id: string
  /** Null on a group heading, which prints only its label. */
  data: T | null
  /** Replaces the first column's text — "Subtotal", "All outlets", a category name. */
  label?: ReactNode
  tone?: 'group' | 'subtotal' | 'total' | 'muted'
}

interface ReportTableProps<T> {
  ariaLabel: string
  columns: ReportColumn<T>[]
  rows: ReportRow<T>[]
  /** Under the body, inside the table's frame — a `ReportPager`. */
  footer?: ReactNode
}

const NUMERIC = new Set(['usd', 'riel', 'whole', 'quantity', 'share'])

const TONE: Record<NonNullable<ReportRow<unknown>['tone']>, string> = {
  group: 'text-muted text-xs font-medium tracking-wide uppercase',
  subtotal: 'font-medium',
  total: 'font-semibold',
  muted: 'text-muted',
}

/**
 * The grid every sales report is laid out in.
 *
 * One component rather than five hand-built tables, for the reason the legacy
 * pack got right and NexQR should keep: the money columns run in the same
 * order on every report — Total, Discount, After discount, VAT, Delivery, Net —
 * so any two can be laid side by side and compared. Five tables each deciding
 * where their discount column goes is how that is lost.
 *
 * Subtotals and the grand total are rows the SERVER computed, passed in as
 * rows. Nothing here adds a column up: a footer summed in the browser would be
 * a second answer to a question the API has already answered, and the two
 * would differ by a cent the first time the rounding fell differently.
 */
export function ReportTable<T>({ ariaLabel, columns, rows, footer }: ReportTableProps<T>) {
  const data = rows.flatMap((row) => (row.data ? [row.data] : []))
  const visible = columns.filter(
    (column) =>
      (!column.hideWhenEmpty || data.some((row) => !isEmpty(column.value(row)))) &&
      (!column.visibleWhen || column.visibleWhen(data)),
  )

  return (
    <Table>
      <Table.ScrollContainer>
        <Table.Content aria-label={ariaLabel}>
          <Table.Header>
            {visible.map((column, index) => (
              <Table.Column
                key={column.id}
                id={column.id}
                isRowHeader={index === 0}
                // One line per header: the table scrolls sideways instead of
                // stacking "Total / Amount" into a two-line heading.
                className={['whitespace-nowrap', isNumeric(column) ? 'text-end' : ''].join(' ')}
              >
                {column.header}
              </Table.Column>
            ))}
          </Table.Header>

          <Table.Body>
            {/* Rows and columns share ONE key space in React Aria's collection, so
                the grand-total row `total` would replace the Total column and the
                header would end before it. Prefixing rows keeps the two apart. */}
            {rows.map((row) => {
              const isSum = row.tone === 'subtotal' || row.tone === 'total'

              // A heading has only its label: one cell across the table, so a
              // long one — an invoice's number, time, outlet and people — reads
              // on one line instead of wrapping down the first column.
              if (!row.data) {
                return (
                  <Table.Row key={row.id} id={`row:${row.id}`}>
                    <Table.Cell colSpan={visible.length}>
                      <span className={['block', row.tone ? TONE[row.tone] : ''].join(' ')}>
                        {row.label}
                      </span>
                    </Table.Cell>
                  </Table.Row>
                )
              }

              return (
                <Table.Row key={row.id} id={`row:${row.id}`}>
                  {visible.map((column, index) => {
                    const isLabel = index === 0 && row.label !== undefined

                    return (
                      <Table.Cell key={column.id}>
                        <span
                          className={[
                            row.tone ? TONE[row.tone] : '',
                            isNumeric(column) ? 'block text-end tabular-nums' : '',
                            // A block's name may be long — "No cashier (QR
                            // ordering) total" — and it wraps rather than
                            // widening the first column for every line under it.
                            isLabel ? 'block min-w-32' : 'whitespace-nowrap',
                          ].join(' ')}
                        >
                          {isLabel
                            ? row.label
                            : row.data
                              ? // A total has figures, not a time or a customer:
                                // its text cells are left empty rather than
                                // dashed, so the eye goes to the sums.
                                isSum && !isNumeric(column) && !column.render
                                ? ''
                                : cell(column, row.data)
                              : ''}
                        </span>
                      </Table.Cell>
                    )
                  })}
                </Table.Row>
              )
            })}
          </Table.Body>
        </Table.Content>
      </Table.ScrollContainer>

      {footer && <Table.Footer>{footer}</Table.Footer>}
    </Table>
  )
}

interface ReportPagerProps {
  page: number
  pageSize: number
  total: number
  /** What a line is — "invoices", "items". */
  noun: string
  onPageChange: (page: number) => void
}

/**
 * Where the reader is in a long report, in the table's own footer.
 *
 * The count is always there, even on a report that fits one page: "1 to 12 of
 * 12 invoices" is the line that tells a manager the table is whole. The
 * buttons appear only once there is somewhere to go.
 */
export function ReportPager({ page, pageSize, total, noun, onPageChange }: ReportPagerProps) {
  const first = total === 0 ? 0 : page * pageSize + 1
  const last = Math.min(total, (page + 1) * pageSize)

  return (
    <div className="flex w-full items-center justify-between gap-3">
      <p className="text-muted text-sm tabular-nums">
        {first} to {last} of {total.toLocaleString('en-US')} {noun}
      </p>

      {total > pageSize && (
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            className="h-8 gap-1 rounded-full px-2 text-sm"
            isDisabled={page === 0}
            onPress={() => onPageChange(Math.max(0, page - 1))}
          >
            <ChevronLeftIcon width={14} height={14} />
            Prev
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-8 gap-1 rounded-full px-2 text-sm font-semibold"
            isDisabled={last >= total}
            onPress={() => onPageChange(page + 1)}
          >
            Next
            <ChevronRightIcon width={14} height={14} />
          </Button>
        </div>
      )}
    </div>
  )
}

function cell<T>(column: ReportColumn<T>, row: T): ReactNode {
  if (column.render) return column.render(row)

  const value = column.value(row)

  switch (column.format) {
    case 'usd':
      return usd(asNumber(value))
    case 'riel':
      return riel(asNumber(value))
    case 'whole':
      return whole(asNumber(value))
    case 'quantity':
      return quantity(asNumber(value))
    case 'share':
      return share(asNumber(value))
    default:
      return value === null || value === undefined || value === '' ? (
        <span className="text-muted">—</span>
      ) : (
        String(value)
      )
  }
}

function isNumeric<T>(column: ReportColumn<T>): boolean {
  return NUMERIC.has(column.format ?? 'text')
}

function isEmpty(value: CellValue): boolean {
  return value === null || value === undefined || value === '' || value === 0
}

function asNumber(value: CellValue): number | null {
  if (value === null || value === undefined || value === '') return null

  return typeof value === 'number' ? value : Number(value)
}
